import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

export const callModeSchema = z.enum(["audio", "video"]);

export const createCallTokenBodySchema = z.object({
  // Optional. Workspace channels (`type='channel'`) MUST send their
  // workspace id; the server uses it as a belt-and-suspenders check
  // against the channel row. DMs (`workspace_id IS NULL`) and the
  // Home view (no active workspace context) can omit it — channel
  // membership alone is the auth gate, and the server derives the
  // actual workspace_id from the channel row anyway.
  workspaceId: z.string().min(1).optional(),
  channelId: z.string().min(1),
  mode: callModeSchema.default("video"),
});

export const createCallTokenSuccessSchema = z.object({
  provider: z.literal("livekit"),
  roomName: z.string().min(1),
  serverUrl: z.string().url(),
  token: z.string().min(1),
  joinUrl: z.string().url(),
  expiresAt: z.number(),
  mode: callModeSchema,
});

export function createCallTokenPath(): string {
  return "/api/calls/token";
}

export async function createCallTokenRequest({
  apiBaseUrl,
  accessToken,
  body,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  body: z.input<typeof createCallTokenBodySchema>;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof createCallTokenSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedBody = createCallTokenBodySchema.parse(body);
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${createCallTokenPath()}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(parsedBody),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to create call token");
  }

  const payload =
    typeof response.json === "function"
      ? await response.json().catch(() => null)
      : null;
  const parsed = createCallTokenSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid call token response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}
